import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Typography } from '@mui/material';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import { SystemStyleObject } from '@mui/system';
import { Editor, EditorContent, EditorContentProps } from '@tiptap/react';
import { memo, useCallback, forwardRef, useMemo } from 'react';
import { useNotify } from 'react-admin';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import { useDebouncedValue } from '@mantine/hooks';
import { stripHtml } from 'string-strip-html';
import { useMediaLibStore } from '../../screens/editorial/asset/components/common/MediaLibraryUtilities/useMediaLibStore';
import { handleArticleInsertion } from '../../screens/editorial/asset/components/common/MediaLibraryUtilities/MediaLibraryInsertHooks';
import { tryParseDropEventAsJson, toCSSContentString } from '../../utility/functions';
import { getBodyContentStyle } from './RichTextInputStyles';
import WordCount from './WordCount/WordCount';
import {
  CustomEditorOnChangeProps,
  useDebouncedOnChange,
} from './utility/useDebouncedOnChange';
import { EditorSetupConfig } from 'screens/editorial/asset/utility/getEditorConfigs';
import { useMergedRef } from '@mantine/hooks';
import { StyleSuggestions } from './StyleGuide/StyleSuggestions';

export type RichTextInputTypes = {
  editor: Editor;
  id?: EditorSetupConfig['id']; // setup id and data-testid
  name?: EditorSetupConfig['name']; // name registers field to react-hook-form!
  type?: EditorSetupConfig['type'];
  maxLength?: EditorSetupConfig['maxLength'];
  showCount?: EditorSetupConfig['showCount'];
  label?: string;
  sx?: SxProps<Theme>;
  debounceMs?: number;
  isDataPrivate?: boolean; // set data-private attribute
  /**
   * Whether or not the input is disabled, may want to set the editable
   * property on the titptap editor to false as well in certain cases.
   **/
  disabled?: EditorContentProps['disabled'];
  autoComplete?: EditorContentProps['autoComplete'];
  spellCheck?: EditorContentProps['spellCheck'];
  isHidden?: EditorContentProps['hidden'];
  editorCSSProperties?: EditorContentProps['style'];
  onFocus?: EditorContentProps['onFocus'];
  onBlur?: EditorContentProps['onBlur'];
  validatorFn?: (value: any, formValues: any) => string | undefined;
  /** override default onChange behavior - validate and set to form state yourself */
  onEditorChange?: ({ editor, transaction, result }: CustomEditorOnChangeProps) => void;

  dynamicPlaceholderFromFieldName?: string; // Field name to mirror
  dynamicPlaceholderFormatter?: (source: string | undefined) => string; // Transform (max length, etc)
  disableDropCursor?: EditorSetupConfig['disableDropCursor']; // Disable DropCursor extension
};

export const richTextLabelTypographySx: SystemStyleObject = {
  fontSize: 21,
  fontWeight: 'bold',
  lineHeight: 'normal',
};

function decodeHTMLEntities(html: string): string {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = html;
  return textarea.value;
}

const RichTextInput = memo(
  forwardRef<HTMLDivElement, RichTextInputTypes>(
    (
      {
        editor,
        id,
        name = '',
        type = 'text',
        label,
        sx,
        maxLength,
        showCount,
        debounceMs = 250,
        isDataPrivate,
        disabled = false,
        autoComplete,
        spellCheck = true,
        isHidden = false,
        editorCSSProperties,
        onFocus,
        onBlur,
        validatorFn,
        onEditorChange,
        dynamicPlaceholderFromFieldName,
        dynamicPlaceholderFormatter,
        disableDropCursor,
      },
      ref,
    ) => {
      const testId = `${id}-rich-text-editor`;
      const theme = useTheme();
      const notify = useNotify();
      const { setSelectedRecords, setReplaceNode } = useMediaLibStore();
      const { control } = useFormContext();
      const hasAiToolkitExt = editor?.extensionManager?.extensions?.some(
        (ext) => ext.name === 'aiToolkit',
      );

      // Register the field with RHF
      const { field, fieldState } = useController({
        control: control,
        name: name,
        rules: {
          validate: (value, formValues) => {
            if (typeof validatorFn === 'function') {
              return validatorFn(value, formValues);
            }
            return undefined;
          },
        },
      });

      // Merge forwarded ref and RHF field ref
      const mergedRef = useMergedRef<HTMLDivElement>(ref, field?.ref);

      /**
       * Default editor change handler if no custom one is provided.
       *
       * Note: Initial tiptap setContents may trigger validation if 'onChange'.
       * This can happen even if current formValue === incoming 'result' value,
       * since RHF will trigger onChange, even if the value is the same. If
       * setting mode onChange to the <Form> this can trigger validation to
       * fire right as a 'create' asset page is loaded with empty defaults...
       *
       * In the future we may add some guards to prevent
       * that if it becomes an issue, such as skipping the
       * onChange call if the getValues(field) is deep equal
       * to the incoming formatted 'result' value.
       *
       * For now, have moved the <Form> mode to 'onTouched' to
       * prevent validation firing on initial setContent, while maintaining
       * the onChange call in case any other listeners are registered.
       * The form should still validate all fields on a submit attempt.
       * Previous code here checked (formState.submitCount && validatorFn)
       * but in debugging the count always appeared to be 0, even after
       * clicking save and submitting the form in create/edit modes...
       **/

      const defaultHandleEditorChange = useCallback(
        async ({ editor, transaction, result }: CustomEditorOnChangeProps) => {
          if (!editor) return;
          // Decode HTML entities ONLY for 'text' or 'textarea' types
          let decodedResult = result;

          if (typeof result === 'string' && (type === 'text' || type === 'textarea')) {
            decodedResult = decodeHTMLEntities(result);
          }

          // Update the RHF field with the result value
          if (field) {
            field?.onChange(decodedResult);
          }
        },
        [field, type],
      );

      // Determine if using default or custom onChange handler
      const usingEditorOnChange = useCallback(
        ({ editor, transaction, result }: CustomEditorOnChangeProps) => {
          if (onEditorChange) {
            return onEditorChange({ editor, transaction, result });
          } else {
            return defaultHandleEditorChange({ editor, transaction, result });
          }
        },
        [defaultHandleEditorChange, onEditorChange],
      );

      // Register determined onchange handler with debounce
      useDebouncedOnChange({
        delay: debounceMs,
        onChange: usingEditorOnChange,
        type: type,
        editor: editor,
      });

      // Dynamic placeholder (mirroring another field)
      const sourceValue: string | undefined = useWatch({
        control,
        name: dynamicPlaceholderFromFieldName || '',
      });
      const computedPlaceholder =
        (dynamicPlaceholderFormatter
          ? dynamicPlaceholderFormatter(sourceValue)
          : (sourceValue ?? '').toString()) || '';

      const scrubbedPlaceholder = stripHtml(computedPlaceholder).result;

      const [debouncedPlaceholder] = useDebouncedValue(scrubbedPlaceholder, 150);

      const cssContentValue = useMemo(
        () => toCSSContentString(debouncedPlaceholder),
        [debouncedPlaceholder],
      );

      const shouldApplyDynamicPlaceholder = Boolean(debouncedPlaceholder?.trim());

      // Handle onBlur events
      const handleOnBlur = useCallback(
        (event: React.FocusEvent<HTMLDivElement, Element>) => {
          // Hook up with RHF field onBlur for form validation
          if (field) {
            field?.onBlur();
          }
          // Call any passed onBlur props
          if (typeof onBlur === 'function') {
            onBlur(event);
          }
        },
        [field, onBlur],
      );

      // Handle onFocus events
      const handleOnFocus = useCallback(
        (event: React.FocusEvent<HTMLDivElement, Element>) => {
          // Call any passed onFocus props
          if (typeof onFocus === 'function') {
            onFocus(event);
          }
        },
        [onFocus],
      );

      // Drop handler switch for handling different drag types
      // see also: https://tiptap.dev/docs/editor/api/extensions/file-handler
      const handleDropEvent = useCallback(
        (event: React.DragEvent<Element>) => {
          event?.preventDefault();
          if (event?.dataTransfer?.types?.includes('application/json')) {
            try {
              const parsedData = tryParseDropEventAsJson(event);
              switch (parsedData?.dragType) {
                case 'MediaLibAssets': {
                  // Prerequisites for handling Media Library ("Asset Library") drops
                  if (
                    parsedData?.records &&
                    Array.isArray(parsedData?.records) &&
                    parsedData?.records.length > 0 && // Ensure at least one record is selected
                    editor?.schema?.nodes?.bloxInline && // Ensure the schema has the bloxInline node
                    name === 'content' // Only handle drops for the 'content' field (body copy)
                  ) {
                    // Call the insertion function with the current drop event
                    const wasSuccessful = handleArticleInsertion(
                      editor,
                      parsedData.records,
                      notify,
                      event, // Pass the DragEvent to the handleArticleInsertion
                    );
                    if (wasSuccessful) {
                      setSelectedRecords([]); // Clear selected records in library
                      setReplaceNode(undefined); // Clear the replaceNode in library
                    } else {
                      throw new Error('Asset library insertion failed.'); // Throw to catch block.
                    }
                  }
                  break;
                }
                // Add more switch cases here for different drag types if we need to.
                default:
                  console.warn(
                    'Unhandled application/json drag type:',
                    parsedData?.dragType,
                  );
              }
            } catch (error) {
              console.error('Failed to parse dropped data:', error);
            }
          }
        },
        [editor, name, notify, setReplaceNode, setSelectedRecords], // Ensure all dependencies are correctly listed here
      );

      if (!editor) {
        return null;
      }

      return (
        <Box
          id="richtextinput-container"
          className="richtextinput-container"
          data-bubble-boundary
          sx={{
            ...getBodyContentStyle(theme),
            ...(sx ? (typeof sx === 'function' ? sx(theme) : sx) : {}),
            ...(shouldApplyDynamicPlaceholder
              ? {
                  // CSS override for dynamic placeholder
                  '--rti-dynamic-placeholder': cssContentValue,
                  '& .tiptap.ProseMirror p.is-editor-empty:first-of-type::before': {
                    content: 'var(--rti-dynamic-placeholder) !important',
                  },
                }
              : {}),
          }}
        >
          {hasAiToolkitExt && <StyleSuggestions editor={editor} />}
          {label && (
            <Typography
              id="rich-text-label"
              sx={richTextLabelTypographySx}
            >
              {String(label)}
            </Typography>
          )}

          <EditorContent
            ref={mergedRef}
            id={id}
            data-testid={testId}
            data-private={isDataPrivate ? true : undefined}
            editor={editor}
            // Position relative required for proper bubble menu placement
            style={{ position: 'relative', ...editorCSSProperties }}
            className={`input-field input-${type} ${fieldState.error ? 'has-error' : ''}`}
            onDrop={handleDropEvent}
            onFocus={handleOnFocus}
            onBlur={handleOnBlur}
            disabled={disabled}
            hidden={isHidden}
            spellCheck={spellCheck}
            autoComplete={autoComplete}
          />

          {fieldState.error && (
            <Box
              className="error-notice"
              sx={{
                color: theme.palette.error.main,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '5px',
              }}
            >
              <ErrorOutlineIcon />
              <span className="error-text">
                {typeof fieldState.error.message === 'object'
                  ? (fieldState.error.message as { message: string })?.message
                  : fieldState.error.message}
              </span>
            </Box>
          )}

          {showCount && (
            <WordCount
              editor={editor}
              maxLength={maxLength}
              display={showCount}
            />
          )}
        </Box>
      );
    },
  ),
);

RichTextInput.displayName = 'RichTextInput';
export default RichTextInput;
